import type { Language } from "../../client/i18n/translations";

// Formats d'affichage du Lite. Montants en $ (devise des comptes prop futures).

export const localeOf = (lang: Language) => (lang === "fr" ? "fr-FR" : "en-US");

export function makeFormat(lang: Language) {
  const locale = localeOf(lang);
  const nf0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const nf2 = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const minus = "−";

  const money = (v: number, opts: { decimals?: boolean; sign?: boolean } = {}) => {
    const { decimals = false, sign = true } = opts;
    const abs = (decimals ? nf2 : nf0).format(Math.abs(v));
    const s = v > 0 && sign ? "+" : v < 0 ? minus : "";
    return lang === "fr" ? `${s}${abs} $` : `${s}$${abs}`;
  };
  const moneyShort = (v: number) => {
    const s = v > 0 ? "+" : v < 0 ? minus : "";
    const a = Math.abs(v);
    if (a >= 1000) return `${s}${(a / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })}k`;
    return `${s}${Math.round(a)}`;
  };
  const pct = (v: number, digits = 0) =>
    `${(v * 100).toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits })}${lang === "fr" ? " %" : "%"}`;
  const num = (v: number, digits = 2) => v.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  const price = (v: number) => v.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const time = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const dateLong = (d: string | Date) => capitalize(new Date(d).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" }));
  const dateShort = (d: string | Date) => new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short" });
  const monthYear = (d: Date) => capitalize(d.toLocaleDateString(locale, { month: "long", year: "numeric" }));
  const monthShort = (d: Date) => d.toLocaleDateString(locale, { month: "short" });
  const duration = (ms: number) => {
    const m = Math.round(ms / 60_000);
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`;
  };
  return { locale, money, moneyShort, pct, num, price, time, dateLong, dateShort, monthYear, monthShort, duration };
}

export type LiteFormat = ReturnType<typeof makeFormat>;

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const dayKey = (iso: string | Date) => {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const pnlClass = (v: number) => (v > 0 ? "text-pnlPositive" : v < 0 ? "text-pnlNegative" : "text-muted-foreground");
